import React, { useEffect, useState, createContext, useContext } from 'react';
import { useToast } from '../components/ui/ToastContainer';
interface User {
  id: string;
  displayName: string;
  email: string;
  avatar?: string;
  level?: string;
  joinDate?: string;
  streak?: number;
  xpPoints?: number;
}
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (name: string, email: string, password: string) => Promise<boolean>;
  loading: boolean;
}
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: async () => false,
  logout: () => {},
  register: async () => false,
  loading: true
});
export const useAuth = () => useContext(AuthContext);
export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast?.() || {
    success: () => {},
    error: () => {}
  };
  useEffect(() => {
    // Check for saved user in localStorage
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);
  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Simple validation
      if (!email || !password) {
        toast.error('Login Error', 'Please enter both email and password');
        setLoading(false);
        return false;
      }
      // For demo, accept any email/password combination
      const mockUser: User = {
        id: '1',
        displayName: 'محمد أحمد',
        email: email,
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
        level: 'Intermediate',
        joinDate: '2023-01-15',
        streak: 7,
        xpPoints: 1250
      };
      setUser(mockUser);
      localStorage.setItem('user', JSON.stringify(mockUser));
      toast.success('Login Successful', 'Welcome back!');
      setLoading(false);
      return true;
    } catch (error) {
      setLoading(false);
      toast.error('Login Error', 'An unexpected error occurred');
      console.error('Login error:', error);
      return false;
    }
  };
  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    toast.success('Logout Successful', 'You have been logged out');
  };
  const register = async (name: string, email: string, password: string) => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      // Simple validation
      if (!name || !email || !password) {
        toast.error('Registration Error', 'Please fill in all fields');
        setLoading(false);
        return false;
      }
      // For demo, accept any registration
      const mockUser: User = {
        id: '1',
        displayName: name,
        email: email,
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
        level: 'Beginner',
        joinDate: new Date().toISOString().split('T')[0],
        streak: 1,
        xpPoints: 100
      };
      setUser(mockUser);
      localStorage.setItem('user', JSON.stringify(mockUser));
      toast.success('Registration Successful', 'Your account has been created');
      setLoading(false);
      return true;
    } catch (error) {
      setLoading(false);
      toast.error('Registration Error', 'An unexpected error occurred');
      console.error('Registration error:', error);
      return false;
    }
  };
  return <AuthContext.Provider value={{
    user,
    isAuthenticated: !!user,
    login,
    logout,
    register,
    loading
  }} data-id="element-1727">
      {children}
    </AuthContext.Provider>;
};